<template>
  <ErrorView :heading="translate('Page not found')" />
  <DialogDrawer
    ref="dialog"
    data-testid="translation-found-dialog"
    placement="centre"
    modal
    @close="
      oppositeLanguagePage = undefined;
      currentLanguagePage = undefined;
    "
    @submit="addRoute"
  >
    <template #title>{{ translate('Translation found') }}</template>
    <p>
      {{
        translate('This page URL was found in Dutch. Would you like to show the translated page?')
      }}
    </p>
    <template #buttons>
      <BootstrapButton
        name="cancel"
        variant="ghost"
        value="cancel"
        formmethod="dialog"
        @click="currentLanguagePage ? navigate(currentLanguagePage) : dialog?.close()"
      >
        {{ translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton
        type="submit"
        name="submit"
        variant="primary"
        value="default"
        :aria-label="translate('Show translated page')"
      >
        {{ translate('Show') }}
      </BootstrapButton>
    </template>
  </DialogDrawer>
</template>
<script setup lang="ts">
import {findTranslationOfSlug, findTranslationOfId} from '@/components/root/error/NotFound';
import DialogDrawer from '@/components/shared/custom/DialogDrawer/DialogDrawer.vue';
import {RouteRecordRaw} from 'vue-router';

const dialog = useTemplateRef<InstanceType<typeof DialogDrawer>>('dialog');
const router = useRouter();
const {translate} = useLocalisation();
const oppositeLanguagePage = ref<SpherePage<SpherePageType>>();
const currentLanguagePage = ref<SpherePage<SpherePageType>>();

const navigate = (page: SpherePage<SpherePageType>) => {
  const prefix = page.meta.type === 'cms.DashboardPage' ? 'core' : 'page';
  const to = router.resolve({name: `${prefix}-${page.meta.id}`});
  if (to.name !== 'not-found') router.push(to); // N.B push to move on from 'not-found'
  dialog.value?.close();
};

/**
 * Instead of switching language & re-fetching all pages, why not dynamically add the route?
 * N.B /api/pages/:id will 404 in the wrong locale. Handle this is `PageView.vue`.
 * @see {@link apollo_portal/src/components/cores/PageView.vue}
 */
const addRoute = async () => {
  const meta = oppositeLanguagePage.value!.meta; // The only core we can navigate to is /home
  if (meta.type === 'cms.DashboardPage' || meta.type === 'cms.CoreIndexPage')
    throw Error('The Home route should be common to both router locales.');
  else {
    // prettier-ignore
    const node: TreeNodePage = {id: meta.id, title: meta.title, meta, expanded: false, disabled: false, children: [], node_type: 'PageNode'};
    const route: RouteRecordRaw = {
      name: `page-${node.meta.id}`,
      path: meta.url_path.slice(node.meta.url_path.indexOf('/', 1)),
      component: () => import('@/components/cores/PageView.vue'),
      props: () => ({meta}),
      meta: {node},
    };
    router.addRoute('core-dynamic', route);
    router.replace(route);
  }
};

// Find a translation for a given route when matched with `NotFoundView.vue`
onMounted(async () => {
  try {
    if (router.currentRoute.value.meta.previousRoute?.name === undefined) {
      const [opposite, current] = await findTranslationOfSlug(router.currentRoute.value);
      oppositeLanguagePage.value = opposite;
      currentLanguagePage.value = current;
      if (oppositeLanguagePage.value) dialog.value?.openModal();
    } else {
      const page = await findTranslationOfId(router.currentRoute.value.meta.previousRoute);
      if (page) navigate(page);
    }
  } catch {
    dialog.value?.close();
  }
});
</script>
